<template>
    <viewer-tab ref="tab" :title="`Microgeometry | ${partLabel}`" :icon="$icons.propsMicro"
                v-loading="loading" element-loading-text="Saving"
                :url-help="$urls.HELP_MODEL_MICRO" :click-x="clickX" :accept="handleAccept"
                @active="handleTabActive">
        <el-button
                slot="header-buttons"
                class="menu-icon-btn"
                type="text"
                aria-label="clear"
                @click="handleTrash">
            <i class="el-icon-delete"/>
        </el-button>
        <div class="grid-cols-split">
            <div class="grid-micro">
                <div class="plot-micro">
                    <div ref="chartContourLeft"/>
                    <!--PLACEHOLDER {micro} add "measured - design"  microgeometry contour plots (green-white-red)-->
                    <!--PLACEHOLDER {micro} line plots-->
                    <!--<div class="pane-2-chart chart-container">
                        &lt;!&ndash;2D profile and lead trace plots&ndash;&gt;
                        <canvas ref="chartProfileLeft"/>
                        <canvas ref="chartLeadLeft"/>
                    </div>-->
                </div>
                <div ref="formLeftWrap" class="form-micro" @scroll="(evt) => syncScroll(evt,'formRightWrap')">
                    <FormMicroDesign ref="formLeft" :model="formLeft" @change="changeMicroLeft"/>
                    <!--PLACEHOLDER {micro} add "measured" microgeometry form for importing a topology dataset-->
                </div>
            </div>
            <div class="grid-micro">
                <div class="plot-micro">
                    <div ref="chartContourRight"/>
                    <!--PLACEHOLDER {micro} line plots-->
                    <!--<div class="pane-2-chart chart-container">
                        &lt;!&ndash;2D profile and lead trace plots&ndash;&gt;
                        <canvas ref="chartProfileRight"/>
                        <canvas ref="chartLeadRight"/>
                    </div>-->
                </div>
                <div ref="formRightWrap" class="form-micro" @scroll="(evt) => syncScroll(evt,'formLeftWrap')">
                    <FormMicroDesign ref="formRight" :model="formRight" @change="changeMicroRight"/>
                </div>
            </div>
        </div>
    </viewer-tab>
</template>

<script>
  import ViewerTab from "../viewer/ViewerTab";
  import FormMicroDesign from "../_gears/FormMicroDesign";
  import Plotly from 'plotly.js-cartesian-dist-min';
  import {assignProps, copyDeep, copyDeepJSON, ResizeListener} from '../../assets/js/utils';
  import math from "../../assets/js/math";
  import microFcn from '../../assets/js/micro';
  import requests from "../../requests";
  import {apolloMutate} from "../../config/apollo";
  import gql from "graphql-tag";

  export default {
    name: "ModelGearMicro",
    components: {FormMicroDesign, ViewerTab},
    props: {
      clickX: Function,
      openId: String,
      name: String,  // unique tab name
    },

    // STATIC DATA
    units: {
      angle: 'deg',
    },
    formFlankInit: {
      profile: {
        slope: '0',
        crown: '0',
        evalLimitLower: '20',
        evalLimitUpper: '80',
        rootRelief: '0',
        rootReliefStart: '20',
        rootReliefType: requests.gear.MicroType.PARABOLIC,
        tipRelief: '0',
        tipReliefStart: '80',
        tipReliefType: requests.gear.MicroType.PARABOLIC,
      },
      lead: {
        slope: '0',
        crown: '0',
        evalLimitLower: '20',
        evalLimitUpper: '80',
        bottomRelief: '0',
        bottomReliefStart: '20',
        bottomReliefType: requests.gear.MicroType.PARABOLIC,
        topRelief: '0',
        topReliefStart: '80',
        topReliefType: requests.gear.MicroType.PARABOLIC,
      },
    },
    formFlankZeros: {
      profile: {
        slope: '0',
        crown: '0',
        rootRelief: '0',
        tipRelief: '0',
      },
      lead: {
        slope: '0',
        crown: '0',
        bottomRelief: '0',
        topRelief: '0',
      },
    },
    dummy: {
      lead: {
        x: [0, 5, 10, 15, 20, 25, 30],
        y: [-10, -5, -1.5, 0, -1.5, -5, -10],
      },
      profile: {
        x: [0, 5, 10, 15, 20, 25, 30],
        y: [-20, -10, -3, 0, -3, -10, -20],
      },
      contour: {
        x: math.linspace(0, 15, 5),
        y: math.linspace(20, 35, 5),
        z: [[-10, -10.625, -12.5, -15.625, -20],
          [-5.625, -6.25, -8.125, -11.25, -15.625],
          [-2.5, -3.125, -5., -8.125, -12.5],
          [-0.625, -1.25, -3.125, -6.25, -10.625],
          [-0, -0.625, -2.5, -5.625, -10]],
      },
    },

    // PLOT SETTINGS
    chart: {
      nullData: math.linspace(0, 1, 50).map(x => {return {x: x, y: 0}}),
    },
    contour: {
      discretization: 12,
      data: {
        type: 'contour',
        autocontour: true,  // reactive contour scale
        contours: {
          coloring: 'heatmap',  // smooth color gradients
        },
        colorbar: {
          tickfont: {
            size: 10,
          }
        },
        colorscale: 'YlGnBu',  // RdBu, YlGnBu
        // https://plotly.com/javascript/colorscales/
        // DO NOT DELETE
        // colorscale: [
        //   ['0.0', 'rgb(49,54,149)'],
        //   ['0.1', 'rgb(59,86,165)'],
        //   ['0.2', 'rgb(69,117,180)'],
        //   ['0.3', 'rgb(116,173,209)'],
        //   ['0.4', 'rgb(171,217,233)'],
        //   ['0.5', 'rgb(224,243,248)'],
        //   ['0.6', 'rgb(254,224,144)'],
        //   ['0.7', 'rgb(253,174,97)'],
        //   ['0.8', 'rgb(244,109,67)'],
        //   ['0.9', 'rgb(215,48,39)'],
        //   ['1.0', 'rgb(165,0,38)'],
        // ],
      },
      layout: {
        showlegend: false,
        margin: {
          l: 60,
          r: 20,
          t: 5,  // avoid text clipping
          b: 33,
          pad: 1,  // gap between axis tick labels and the axis itself
        },
        xaxis: {
          // PLACEHOLDER {micro} create global settings (e.g. add to $defaults) for layout properties like this
          title: 'Facewidth (mm)',
          titlefont: { size: 11 },
          tickfont: { size: 10 },
        },
        yaxis: {
          title: 'Roll angle (deg)',
          titlefont: { size: 11 },
          tickfont: { size: 10 },
        },
      },
      config: {
        // MODE BAR
        displayModeBar: false,
        // displaylogo: false,
        // modeBarButtonsToRemove: ['sendDataToCloud'],

        // DYNAMIC FEATURES
        responsive: true,  // responsive to window resize
        staticPlot: false,  // enables zoom, hover, etc.
        scrollZoom: false,
      },
    },
    line: {
      profile: {
        data: {
          type: 'scatter',
          mode: 'lines',
          name: 'Profile',
          line: {
            color: 'rgb(219, 64, 82)',  // raspberry red
            shape: 'spline',
            smoothing: 1.0,  // valid values: 0 to 1.3, default=1.0
          },
        },
        lowerLimit: {
          type: 'scatter',
          mode: 'lines',
          name: 'Lower Limit',
          line: {
            color: 'rgb(100, 100, 100)',  // dark gray
            dash: 'dot'
          },
        },
        upperLimit: {
          type: 'scatter',
          mode: 'lines',
          name: 'Upper Limit',
          line: {
            color: 'rgb(100, 100, 100)',  // dark gray
            dash: 'dot'
          },
        },
      },
      lead: {
        data: {
          type: 'scatter',
          mode: 'lines',
          name: 'Lead',
          line: {
            color: 'rgb(31, 119, 180)',  // default blue
            shape: 'spline',
            smoothing: 1.0,  // valid values: 0 to 1.3, default=1.0
          },
        },
        lowerLimit: {
          type: 'scatter',
          mode: 'lines',
          name: 'Lower Limit',
          line: {
            color: 'rgb(100, 100, 100)',  // dark gray
            dash: 'dot'
          },
        },
        upperLimit: {
          type: 'scatter',
          mode: 'lines',
          name: 'Upper Limit',
          line: {
            color: 'rgb(100, 100, 100)',  // dark gray
            dash: 'dot'
          },
        },
      },
    },

    data() {
      return {
        // APOLLO
        part: null,
        gear: null,
        gearId: '',
        loading: false,

        // FORMS
        formLeft: copyDeepJSON(this.$options.formFlankInit),
        formRight: copyDeepJSON(this.$options.formFlankInit),
        ignoreScrollSync: false,

        // PLOTS
        // Layout data is made reactive to allow:
        //  1. changing units (SI, Imperial)
        //  2. changing scales (roll angle, diameter)
        // chartProfile: null,
        // chartLead: null,
        resizeListener: null,
        ignoreResize: false,

        // PLOTLY DEPRECATED
        // profile: {
        //   data: {
        //     x: [20, 30, 40],
        //     y: [0, 0, 0],
        //   },
        //   lowerLimit: {
        //     x: [22.5, 22.5],
        //     y: [-1, 1],
        //   },
        //   upperLimit: {
        //     x: [37.5, 37.5],
        //     y: [-1, 1]
        //   },
        //   layout: {
        //     xaxis: {
        //       title: 'Roll angle (deg)',
        //     },
        //     yaxis: {
        //       title: 'Profile (um)',
        //     },
        //   }
        // },
        // lead: {
        //   data: {
        //     x: [0, 15, 30],
        //     y: [0, 0, 0],
        //   },
        //   lowerLimit: {
        //     x: [5, 5],
        //     y: [-1, 1],
        //   },
        //   upperLimit: {
        //     x: [25, 25],
        //     y: [-1, 1]
        //   },
        //   layout: {
        //     xaxis: {
        //       title: 'Facewidth (mm)',
        //     },
        //     yaxis: {
        //       title: 'Lead (um)',
        //     },
        //   }
        // },
      }
    },
    computed: {
      partLabel() {
        return this.part ? this.$handlers.model.partLabel(this.part) : '';
      },
      flankLimits() {
        const {formRollAngle, tipRollAngle} = this.$handlers.eng.profileLimits(this.gear);

        return {
          facewidth: this.gear.geom.facewidth,
          formRollAngle,
          tipRollAngle
        }
      },
      contourData() {
        // contour data without modification;  XY limits based on gear flank
        const n = this.$options.contour.discretization;
        const {facewidth, formRollAngle, tipRollAngle} = this.flankLimits;
        return {
          ...this.$options.contour.data,
          x: math.linspace(0, facewidth, n),
          y: math.linspace(formRollAngle, tipRollAngle, n),
          z: math.matrix(n, n, 0),
        }
      },
    },
    apollo: {
      gear: {
        skip() { return !this.gearId },
        fetchPolicy: 'cache-and-network',
        variables() { return { Id: this.gearId } },
        query: gql`query getGearMicro ($Id: ID!) {
            gear: gearDetail (Id: $Id) {
              Id
              name
              geom {
                ${requests.gear.GearDetailGeom}
              }
              micro {
                ${requests.gear.GearMicro}
              }
              tooth {
                ${requests.eng.ToothFinishedRootParams}
              }
            }
          }`,
        result({ data }) {
          // deep copy to avoid mutation of Apollo cache data
          let micro = copyDeep(data.gear.micro);

          // map to form data
          this.mapMicroForms(micro);

          // create plots
          this.newPlots();
        },
      },
    },

    mounted() {
      // Called every time the tab is opened.
      // Also see @loaded event handler.
      //
      // NOTES:
      //    1. Parts retrieved with apollo watcher instead of here so that it is reactive to part CRUD.
      //    2. Dialog windows do not benefit from such reactivity, but a viewer tab may benefit.

      // get part being edited
      this.part = this.$store.getters.getPart(this.openId);
      this.$store.commit('SET_VIEWER_TAB_TITLE', this.partLabel);

      // trigger query of gear to edit
      this.gearId = this.part.obj.Id;
    },
    methods: {
      syncScroll(evt, syncRef) {
        // prevent recursive scroll events
        const ignore = this.ignoreScrollSync;
        this.ignoreScrollSync = false;
        if (ignore) return;
        this.ignoreScrollSync = true;

        // sync scroll position
        const elementToSync = this.$refs[syncRef];
        elementToSync.scrollTop = evt.target.scrollTop;
      },
      handleTabActive() {
        this.onViewerResize();
      },

      // APOLLO
      async mutateMicro() {
        // API mutation to update existing gear with the specified microgeometry data.
        // Apollo cache automatically updates.

        // synthesize payload for left flank
        const {profile: profileLeft, lead: leadLeft} = this.convertLimits(
          this.formLeft.profile, this.formLeft.lead, false);

        // synthesize payload for right flank
        const {profile: profileRight, lead: leadRight} = this.convertLimits(
          this.formRight.profile, this.formRight.lead, false);

        const payload = {
          Id: this.gearId,
          micro: {
            leadLeft: leadLeft,
            leadRight: leadRight,
            profileLeft: profileLeft,
            profileRight: profileRight,
          }
        };

        // API mutation call
        this.loading = true;
        const mutate = {
          variables: {
            data: payload,
          },
          mutation: gql`mutation updateGearDetailMicro ($data: UpdateGearDetailInput!) {
            updateGearDetail (data: $data) {
              gearDetail {
                Id
                micro {
                  ${this.$requests.gear.GearMicro}
                }
              }
            }
          }`,
        };
        await apolloMutate(mutate).finally(() => this.loading = false);
      },
      mapMicroForms(micro) {
        // Map microgeometry data from API schema to forms.
        // micro can be null if it has not been defined for a gear.

        if (micro) {
          // microgeometry is defined
          let {profileLeft, profileRight, leadLeft, leadRight} = micro;

          // convert limits to percentages
          ({profile: profileLeft, lead: leadLeft} = this.convertLimits(profileLeft, leadLeft, true));
          ({profile: profileRight, lead: leadRight} = this.convertLimits(profileRight, leadRight, true));

          // assign form data; avoid assigning __typename
          assignProps(this.formLeft.profile, profileLeft);
          assignProps(this.formLeft.lead, leadLeft);

          assignProps(this.formRight.profile, profileRight);
          assignProps(this.formRight.lead, leadRight);
        }
      },
      convertLimits(profile, lead, toPercentages=false) {
        // Convert microgeometry relief limits to/from percentages.
        // API expects decimals, forms expect percentages.
        // Returns clones of input objects with converted values.

        const multiplier = toPercentages ? 100 : 1 / 100;
        const profileConvert = {...profile};
        const leadConvert = {...lead};

        profileConvert.evalLimitLower *= multiplier;
        profileConvert.evalLimitUpper *= multiplier;
        profileConvert.rootReliefStart *= multiplier;
        profileConvert.tipReliefStart *= multiplier;

        leadConvert.evalLimitLower *= multiplier;
        leadConvert.evalLimitUpper *= multiplier;
        leadConvert.bottomReliefStart *= multiplier;
        leadConvert.topReliefStart *= multiplier;

        return {profile: profileConvert, lead: leadConvert};
      },

      // FORMS
      zeroForms() {
        // Set modification magnitudes to zero.
        // Evaluation limits and relief start values are unchanged.

        assignProps(this.formLeft, this.$options.formFlankZeros);
        assignProps(this.formRight, this.$options.formFlankZeros);
      },
      changeMicroLeft() {
        this.updatePlotsLeft();
      },
      changeMicroRight() {
        // Right flank microgeometry changed, try to update plots.
        this.updatePlotsRight();
      },
      handleTrash() {
        // Clear forms and update plots.

        this.zeroForms();
        this.updatePlotsLeft();
        this.updatePlotsRight();
      },
      async handleAccept() {
        // If valid forms, call API mutation, then close tab.
        // If invalid, notification issued via validate methods.

        const validLeft = await this.$refs.formLeft.validate();
        const validRight = await this.$refs.formRight.validate();

        // DEV ONLY
        // console.log('validLeft', validLeft);
        // console.log('validRight', validRight);

        if (validLeft && validRight) {
          await this.mutateMicro();
          this.clickX();
        }
      },

      // PLOTS
      newPlots() {
        // Create new line charts and contour plot.
        // Returns nothing.

        // PLACEHOLDER {micro} create profile chart
        // const ctxProfile = this.$refs.chartProfile.getContext('2d');
        // this.chartProfile = this.newChart(ctxProfile);

        // PLACEHOLDER {micro} create lead chart
        // const ctxLead = this.$refs.chartLead.getContext('2d');
        // this.chartLead = this.newChart(ctxLead);

        // create contour plots
        this.updatePlotsLeft();
        this.updatePlotsRight();

        // start event listeners for resizing renderer; windowResize=false required to avoid recursion
        const viewer = document.getElementById('viewer');
        this.resizeListener = new ResizeListener(viewer, this.onViewerResize, 0, false);
      },
      newChart() {
        // Create new chart.js object, given a 2d context.

        // PLACEHOLDER {micro} creates new line chart; arg: ctx
        // return new Chart(ctx, {
        //   type: 'line',
        //   data: {
        //     datasets: [{
        //       ...this.$defaults.chart.lineDataset,
        //       data: this.$options.chart.nullData,
        //       fill: 'start',
        //     }],
        //   },
        //   options: this.$defaults.chart.options,
        // });
      },
      drawContourLeft(z) {
        // Draw contour plot of left flank.
        // Creates new plot or updates existing plot.
        // https://plotly.com/javascript/plotlyjs-function-reference

        const data = z ? {...this.contourData, z} : {...this.contourData};
        Plotly.react(this.$refs.chartContourLeft, [data], this.$options.contour.layout, this.$options.contour.config);
      },
      drawContourRight(z) {
        // Draw contour plot of right flank.
        // Creates new plot or updates existing plot.
        // https://plotly.com/javascript/plotlyjs-function-reference

        const data = z ? {...this.contourData, z} : {...this.contourData};
        Plotly.react(this.$refs.chartContourRight, [data], this.$options.contour.layout, this.$options.contour.config)
      },
      updatePlotsLeft() {
        // Update left flank plots with current form data.
        // https://plotly.com/javascript/plotlyjs-function-reference
        // PLACEHOLDER {micro} update line chart

        // convert limits to decimals
        const {profile, lead} = this.convertLimits(this.formLeft.profile, this.formLeft.lead, false);

        // update contour plot
        const z = this.computeContourZ(profile, lead);
        this.drawContourLeft(z);
      },
      updatePlotsRight() {
        // Update right flank plots with current form data.
        // https://plotly.com/javascript/plotlyjs-function-reference
        // PLACEHOLDER {micro} update line chart

        // convert limits to decimals
        const {profile, lead} = this.convertLimits(this.formRight.profile, this.formRight.lead, false);

        // update contour plot
        const z = this.computeContourZ(profile, lead);
        this.drawContourRight(z);
      },
      computeContourZ(profile, lead) {
        // Compute z-data for use with contour plot.
        // Sign is flipped such that negative z indicates material removal.
        // Returns an array of arrays.

        const n = this.$options.contour.discretization;
        return microFcn.flankReliefCoord(profile, lead, n, n, -1);
      },
      onViewerResize() {
        // trigger window resize event; plotly responds to this event
        window.dispatchEvent(new Event('resize'));
      },
    },
    beforeDestroy() {
      // remove resize listener and observer
      this.resizeListener.destroy();

      // remove plotly data
      Plotly.purge(this.$refs.chartContourLeft);
      Plotly.purge(this.$refs.chartContourRight);
    },
  }
</script>

<style scoped src="../../assets/css/form.css"/>
<style scoped lang="scss" src="../../assets/css/viewer-tab.scss"/>
<style scoped lang="scss">
    .grid-micro {
        display: grid;
        grid-template-rows: 50% calc(50vh - var(--ribbon-height));
        /* NOTE: 50% 50% does not work due to el-form styles */
    }
    .plot-micro {
        padding: .5rem 0;
    }
    .form-micro {
        overflow-y: auto;
        padding: .75rem .5rem 0;
        margin: .5rem .75rem 0;
        border-top: var(--border-color) solid 4px;
    }

    /* PLOTLY - https://community.plotly.com/t/plot-sizing-problems/1620/22 */
    .js-plotly-plot,
    .plot-container {
        height: 100%;
    }
</style>
